import { createTheme } from '@mui/material/styles';
import colors from 'assets/scss/_themes-vars.module.scss';
import themeTypography from './typography';

export default function themes(customization) {
  const dark = customization.theme === 'dark';
  const surface = dark ? '#191C23' : '#FFFFFF';
  const background = dark ? '#121419' : '#F7F8FA';
  const text = dark ? '#EEF0F6' : '#20232D';
  const muted = dark ? '#A7AEBD' : '#626B7C';
  const border = dark ? '#303541' : '#E6E8EE';
  const accent = dark ? '#A49CFF' : '#635BFF';
  const soft = dark ? '#29263F' : '#F0EEFF';
  const option = {
    customization,
    colors,
    mode: customization.theme,
    heading: text,
    textDark: text,
    darkTextPrimary: text,
    darkTextSecondary: muted,
    background,
    menuButton: soft,
    menuButtonColor: accent,
    menuChip: soft,
    grey500: muted
  };
  const theme = createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: {
        main: accent,
        dark: dark ? '#C2BBFF' : '#544BDA',
        light: soft,
        contrastText: dark ? '#121419' : '#FFFFFF',
        200: soft,
        800: accent
      },
      secondary: { main: accent, dark: accent, light: soft, 200: soft, 800: accent },
      success: { main: dark ? '#62CBA2' : '#18835D', light: dark ? '#153B30' : '#EAF6EF' },
      warning: { main: dark ? '#E9B76C' : '#9B6315', light: dark ? '#3C3020' : '#FFF5E7' },
      error: { main: dark ? '#F2919C' : '#BF4754', light: dark ? '#3F252D' : '#FCEEF0' },
      orange: { main: '#9B6315', light: '#FFF5E7', dark: '#86530D' },
      dark: { main: '#191C23', dark: '#121419', light: '#A7AEBD', 800: '#121419', 900: '#121419' },
      text: { primary: text, secondary: muted, dark: text },
      divider: border,
      background: { default: background, paper: surface },
      grey: { 50: background, 100: border, 500: muted, 600: muted, 700: text, 900: text }
    },
    shape: { borderRadius: 10 },
    typography: {
      ...themeTypography(option),
      fontFamily: customization.fontFamily,
      body1: { fontSize: '0.875rem', lineHeight: 1.65 },
      body2: { fontSize: '0.875rem', lineHeight: 1.65 },
      button: { textTransform: 'none', fontWeight: 600 },
      h1: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.04em' },
      h2: { fontSize: '1.5rem', fontWeight: 650, letterSpacing: '-0.03em' },
      h3: { fontSize: '1.125rem', fontWeight: 600 },
      mainContent: { backgroundColor: background },
      CardWrapper: { backgroundColor: surface },
      customInput: { marginTop: 1.5, marginBottom: 1.5 },
      otherInput: { marginTop: 2, marginBottom: 2 }
    },
    mixins: { toolbar: { minHeight: 64, '@media (min-width:600px)': { minHeight: 64 } } },
    transitions: { duration: { shortest: 120, shorter: 150, short: 180, standard: 180, enteringScreen: 180, leavingScreen: 150 } },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { fontVariantNumeric: 'tabular-nums' },
          a: { color: 'inherit' },
          '*:focus-visible': { outline: `3px solid ${accent}`, outlineOffset: 3 },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important'
            }
          }
        }
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 6, minHeight: 38, padding: '8px 16px' },
          sizeLarge: { minHeight: 46 },
          outlined: { borderColor: border }
        }
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 6, padding: 10 } } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: { styleOverrides: { root: { border: `1px solid ${border}`, boxShadow: '0 2px 6px rgba(20,24,40,.025)' } } },
      MuiCardHeader: { styleOverrides: { root: { padding: '20px 24px' } } },
      MuiCardContent: { styleOverrides: { root: { padding: 24 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 6, backgroundColor: surface },
          input: { padding: '12px 14px' },
          notchedOutline: { borderColor: border }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${border}`, padding: '16px 20px', textAlign: 'left', fontSize: 13 },
          head: { color: muted, backgroundColor: background, fontWeight: 600, padding: '12px 20px', whiteSpace: 'nowrap' }
        }
      },
      MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: dark ? '#222631' : '#FAFAFD' } } } },
      MuiTableContainer: { styleOverrides: { root: { overflowX: 'auto !important' } } },
      MuiTablePagination: {
        defaultProps: {
          labelRowsPerPage: '每页',
          labelDisplayedRows: ({ from, to, count }) => `${from}–${to} / ${count === -1 ? '更多' : count}`
        },
        styleOverrides: { toolbar: { flexWrap: 'wrap', padding: 8 } }
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 5, fontWeight: 500 }, sizeSmall: { height: 24, fontSize: 12 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 8 }, message: { lineHeight: 1.7 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 12, border: `1px solid ${border}` } } },
      MuiDialogTitle: { styleOverrides: { root: { padding: 24, fontSize: 20 } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 8 } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            '&.Mui-selected': { color: dark ? accent : '#544BDA', backgroundColor: soft },
            '&.Mui-selected:hover': { backgroundColor: soft }
          }
        }
      },
      MuiTooltip: { defaultProps: { arrow: true } }
    }
  });
  return theme;
}
